/**
 * The clone sibyl reviews, and the byte-exact read that every quote gate depends on.
 *
 * ⛔ SIBYL NEVER REVIEWS A TREE SOMEBODY ELSE IS WORKING IN. It prepares its own clone, under its
 * own root, outside every checkout. `docs/spec/config.md` [repo]: a review clone inside a working
 * tree is one `git add -A` from being committed, and parallel sessions mutate the tree underneath
 * the review so findings cite lines that were never in the pull request.
 *
 * ⛔ NEVER AGAINST A FORBIDDEN TREE — a shared monorepo with many worktrees; `git status` and `git worktree list` time
 * out. `FORBIDDEN_TREES` refuses it before a single git process starts.
 * docs/ARCHITECTURE.md, "Repository access".
 *
 * ⚠️ That section was reworded on 2026-09-15 to say "the shared application checkout" rather than
 * naming the path, on the grounds that anyone on the team may read this repository. The guard still
 * needs a literal to compare against, so the path lives here. If the same policy is meant to apply
 * to source, this constant moves into config — that is a decision, not something to quietly do.
 *
 * ⛔ NEVER `git worktree prune`, and never any `git worktree` subcommand at all. That deregisters
 * OTHER sessions' worktrees on a shared `.git`, and they are not our object to clean up.
 * `repo.never_prune_worktrees = true` is the knob; this module simply has no such code path, and
 * `clone.test.ts` asserts that on the compiled source.
 *
 * ⛔ EVERY GIT CALL IS `execFile` WITH AN ARGUMENT ARRAY, NEVER A SHELL STRING. A branch name, a
 * remote URL and a repository slug all arrive from outside; one of them containing `;` or a
 * backtick in a shell string is arbitrary code execution on the reviewer's machine.
 *
 * ⭐ WE REVIEW THE MERGE REF, NOT THE HEAD. docs/ARCHITECTURE.md: they differ, and the merge result is what
 * ships. When the merge ref is unborn — GitHub does not publish one while the pull request
 * conflicts — we fall back to the head ref AND STAMP THAT ON THE RESULT, because the same finding
 * means a different thing on each: on the merge ref it is "this breaks what ships", on the head ref
 * it is "this breaks in isolation, and we could not see the integration".
 */

import { execFile } from "node:child_process"
import { createHash } from "node:crypto"
import {
  existsSync, mkdirSync, readdirSync, readFileSync, realpathSync, rmSync, writeFileSync,
  type Dirent,
} from "node:fs"
import { delimiter, dirname, isAbsolute, join, resolve, sep } from "node:path"
import { fileURLToPath } from "node:url"
import { expandPath } from "../config/load.js"
import type { SibylConfig } from "../config/schema.js"
import type { Provenance } from "../types/finding.js"

/** Only the slice of the config this module reads, so a caller can hand us a partial. */
export type CloneConfig = Pick<SibylConfig, "repo">

export type CloneErrorCode =
  | "forbidden-tree"
  | "root-inside-checkout"
  | "bad-repo"
  | "bad-pr-number"
  | "bad-ref-template"
  | "bad-duration"
  | "no-ref"
  | "dirty-clone-path"
  | "path-escape"
  | "git-failed"

/** ⛔ A code, not only a message. A caller branching on prose breaks the first time we reword it. */
export class CloneError extends Error {
  constructor(
    message: string,
    readonly code: CloneErrorCode,
  ) {
    super(message)
    this.name = "CloneError"
  }
}

/**
 * ⛔ Trees sibyl refuses to touch, at any depth, whatever the config says.
 *
 * Set `SIBYL_FORBIDDEN_TREES` (path-delimiter separated) to the checkouts a review must never
 * read or write — typically a large shared monorepo with many worktrees on one `.git`, where
 * `git status` and `git worktree list` time out and anything that prunes deregisters other people's
 * worktrees. This is a hard refusal rather than a warning because the damage is to other people's
 * work, not ours. Read at call time, so a change to the environment takes effect without a restart.
 *
 * ⚠️ Compared case-insensitively: on a case-insensitive filesystem `/srv/Repo` and `/srv/repo` are
 * the same directory.
 */
export const FORBIDDEN_TREES = (): readonly string[] =>
  (process.env.SIBYL_FORBIDDEN_TREES ?? "").split(delimiter).map((t) => t.trim()).filter(Boolean)

/** Lives inside `.git/`, which `git clean -ffdx` never touches, so reuse bookkeeping survives a checkout. */
const STAMP_REL = join(".git", "sibyl-clone.json")
const STAMP_MARKER = "sibyl-clone"

/**
 * ⛔ Nothing is deleted without this marker. Pruning reads it and skips any directory that does not
 * carry one — a clone root that a person also keeps their own checkouts in must survive us.
 */
interface CloneStamp {
  readonly marker: typeof STAMP_MARKER
  readonly remote: string
  readonly lastUsedMs: number
  readonly lastPr: number
  readonly lastRef: string
}

/** What a prepared clone is, and everything a finding needs to be interpreted correctly. */
export interface PreparedClone {
  /** Absolute path to the checked-out tree. */
  readonly path: string
  /** The commit actually checked out, detached. Resolved from the tree, never assumed. */
  readonly sha: string
  /** The remote ref we reviewed — the merge ref, or the fallback. */
  readonly ref: string
  /** ⭐ True when the merge ref was unborn. Changes what every finding from this run means. */
  readonly usedFallbackRef: boolean
  /** True when an existing clone was re-fetched rather than cloned again. */
  readonly reused: boolean
  /** Directory names removed from the clone root because they were idle past the window. */
  readonly pruned: readonly string[]
}

/**
 * The provenance fields a finding carries from the clone.
 *
 * ⭐ Exists so `usedFallbackRef` cannot be forgotten at the call site. Every finding produced from
 * this clone is stamped from one place.
 */
export function reviewStamp(c: PreparedClone): Pick<Provenance, "reviewedSha" | "usedFallbackRef"> {
  return { reviewedSha: c.sha, usedFallbackRef: c.usedFallbackRef }
}

// ── git, as a process and not a shell ──────────────────────────────────────────────────────────

/**
 * ⚠️ Long enough for a cold fetch of a large repository over a slow link, short enough that a hung
 * transport does not hold a review open forever. There is nobody at the keyboard to notice.
 */
const GIT_TIMEOUT_MS = 600_000
/** `ls-remote` on a busy repository is comfortably past execFile's 1 MB default. */
const GIT_MAX_BUFFER = 64 * 1024 * 1024

/**
 * ⛔ `core.hooksPath` is pointed at a non-directory so no hook can ever run. We check out branches
 * that anyone can push; a repository-supplied hook firing during our checkout would be code
 * execution from a pull request under review.
 */
const GIT_GLOBAL_FLAGS = ["-c", "core.hooksPath=/dev/null"] as const

/**
 * The credential, handed to git through the ENVIRONMENT and never through argv.
 *
 * ⛔ NOT `-c http.extraheader=…` AND NOT `https://token@github.com/…`. Both put the token in the
 * process argument list, which any user on the machine can read from `ps` or `/proc/<pid>/cmdline`.
 * docs/SECURITY.md names a credential in an argument list as a leak in exactly those terms, and the
 * security dimension's own prompt tells the model to report it. Git's `GIT_CONFIG_COUNT` mechanism
 * sets the identical config from the environment instead.
 *
 * ⛔ SCOPED TO THE HOST BEING FETCHED FROM. An unscoped header — or a `url.…insteadOf` rewrite —
 * sends this repository's token to whatever host a redirect or a submodule `url =` happens to name.
 *
 * ⚠️ Base64 here is encoding, not encryption. The value is exactly as secret as the token; the only
 * property being bought is that it never reaches argv.
 */
export function credentialEnv(remote: string, token: string | undefined): NodeJS.ProcessEnv {
  if (token === undefined || token === "") return {}
  let origin: string
  try {
    const u = new URL(remote)
    // ssh carries its own key material; there is nothing to inject and no header to set.
    if (u.protocol !== "https:") return {}
    origin = `${u.protocol}//${u.host}`
  } catch {
    return {}
  }
  const basic = Buffer.from(`x-access-token:${token}`, "utf8").toString("base64")
  return {
    GIT_CONFIG_COUNT: "1",
    GIT_CONFIG_KEY_0: `http.${origin}/.extraheader`,
    GIT_CONFIG_VALUE_0: `Authorization: Basic ${basic}`,
  }
}

interface GitRun {
  readonly stdout: string
  readonly stderr: string
  readonly exitCode: number
}

function runGit(cwd: string | undefined, args: readonly string[], extraEnv: NodeJS.ProcessEnv = {}): Promise<GitRun> {
  return new Promise<GitRun>((done) => {
    execFile(
      "git",
      [...GIT_GLOBAL_FLAGS, ...args],
      {
        cwd,
        env: {
          ...process.env,
          ...extraEnv,
          // ⛔ Nobody is at the keyboard. Without this a private remote with no usable credential
          // blocks on a username prompt until the timeout, and the run looks like a hang rather
          // than an auth failure.
          GIT_TERMINAL_PROMPT: "0",
          GIT_ASKPASS: "",
          // Error text is read by a person, never matched by us — but a stable locale keeps a
          // reported failure comparable between machines.
          LC_ALL: "C",
        },
        timeout: GIT_TIMEOUT_MS,
        maxBuffer: GIT_MAX_BUFFER,
        windowsHide: true,
        encoding: "utf8",
      },
      (err, stdout, stderr) => {
        if (err === null) {
          done({ stdout, stderr, exitCode: 0 })
          return
        }
        const code = (err as NodeJS.ErrnoException).code
        done({ stdout, stderr, exitCode: typeof code === "number" ? code : -1 })
      },
    )
  })
}

/** Run git and throw with the command and stderr attached. A git failure is never swallowed. */
async function mustGit(cwd: string | undefined, args: readonly string[], extraEnv: NodeJS.ProcessEnv = {}): Promise<string> {
  const r = await runGit(cwd, args, extraEnv)
  if (r.exitCode !== 0) {
    throw new CloneError(
      // ⚠️ `args` is safe to echo; the credential lives in the environment precisely so that it is
      // not in here. If a token is ever added to argv, this message publishes it.
      `git ${args.join(" ")} failed (exit ${r.exitCode}): ${r.stderr.trim() || r.stdout.trim()}`,
      "git-failed",
    )
  }
  return r.stdout
}

// ── Where the clone goes ───────────────────────────────────────────────────────────────────────

/**
 * ⛔ Refuses a path at or under a forbidden tree before any process starts.
 *
 * Checked on BOTH the clone root and a local remote: the damage at a forbidden tree comes from
 * reading it as much as from writing to it.
 */
export function assertNotForbiddenTree(path: string, what: string): void {
  const norm = resolve(path).toLowerCase()
  for (const tree of FORBIDDEN_TREES()) {
    const t = resolve(tree).toLowerCase()
    if (norm === t || norm.startsWith(t + sep)) {
      throw new CloneError(
        `refusing to use ${what} ${path}: ${tree} is a forbidden tree (SIBYL_FORBIDDEN_TREES)`,
        "forbidden-tree",
      )
    }
  }
}

/**
 * ⛔ The clone root must not sit inside any git checkout, at any depth.
 *
 * A review clone under someone's working tree gets swept up by `git add -A`, shows up in their
 * `git status`, and makes every `git grep` in that tree return our copy of somebody else's code.
 * Walking to the filesystem root is cheap and is the only check that catches a root six levels
 * deep in a monorepo.
 */
export function assertRootOutsideEveryCheckout(root: string): void {
  let dir = resolve(root)
  for (;;) {
    if (existsSync(join(dir, ".git"))) {
      throw new CloneError(
        `clone_root ${root} is inside the git checkout at ${dir}. ` +
          `The clone root must live outside every checkout — a review clone in a working tree is one ` +
          `\`git add -A\` from being committed.`,
        "root-inside-checkout",
      )
    }
    const parent = dirname(dir)
    if (parent === dir) return
    dir = parent
  }
}

/**
 * The remote to fetch from.
 *
 * ⚠️ A bare `owner/name` is a GitHub slug. A local repository must be absolute or start with `./` —
 * otherwise `some/dir` is ambiguous with a slug, and guessing wrong silently reviews a stranger's
 * repository instead of the one on disk.
 */
export function remoteUrlFor(repo: string): string {
  const r = repo.trim()
  if (r === "") throw new CloneError("no repository given and repo.slug is empty", "bad-repo")
  if (/^file:\/\//i.test(r)) return r
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(r)) return r
  if (/^[^/\\@]+@[^/\\:]+:/.test(r)) return r // scp-like: git@github.com:owner/name.git
  if (r.startsWith("/") || r.startsWith("./") || r.startsWith("../") || r.startsWith("~")) {
    return expandPath(r)
  }
  if (/^[A-Za-z0-9._-]+\/[A-Za-z0-9._-]+$/.test(r)) return `https://github.com/${r}.git`
  throw new CloneError(
    `cannot read ${JSON.stringify(repo)} as a repository. Use "owner/name", a URL, or an absolute path.`,
    "bad-repo",
  )
}

/** The local path a remote refers to, or undefined when it is not local. Used only by the guards. */
function localPathOf(remote: string): string | undefined {
  if (/^file:\/\//i.test(remote)) {
    try {
      return fileURLToPath(remote)
    } catch {
      return undefined
    }
  }
  return isAbsolute(remote) ? remote : undefined
}

/**
 * The directory name for a remote.
 *
 * ⚠️ The hash is not decoration. Two forks are both `owner/sibyl`; a slug that differs only in case
 * is the same directory on a case-insensitive filesystem. Either collision makes one review read the
 * other repository's code and cite lines that exist, so the name stays readable AND unambiguous.
 */
export function cloneDirName(remote: string): string {
  const trimmed = remote.replace(/\.git$/i, "").replace(/[/\\]+$/, "")
  const parts = trimmed.split(/[/\\]+/).filter((p) => p !== "" && !p.includes(":"))
  const tail = parts.slice(-2).join("__") || "repo"
  const safe = tail.replace(/[^A-Za-z0-9._-]/g, "-").slice(0, 60)
  const hash = createHash("sha256").update(remote).digest("hex").slice(0, 10)
  return `${safe}-${hash}`
}

/**
 * `"14d"` → milliseconds.
 *
 * ⭐ `"0"` disables pruning entirely. The safe reading of "no window configured" is "delete
 * nothing", never "delete everything".
 */
export function parseDuration(spec: string): number {
  const m = /^\s*(\d+)\s*(s|m|h|d|w)?\s*$/.exec(spec)
  if (m === null) throw new CloneError(`cannot read ${JSON.stringify(spec)} as a duration`, "bad-duration")
  const n = Number(m[1])
  if (n === 0) return 0
  const unit = m[2]
  if (unit === undefined) {
    throw new CloneError(
      `duration ${JSON.stringify(spec)} has no unit — write 14d, 6h, 30m or 0`,
      "bad-duration",
    )
  }
  const ms: Record<"s" | "m" | "h" | "d" | "w", number> = {
    s: 1000, m: 60_000, h: 3_600_000, d: 86_400_000, w: 604_800_000,
  }
  return n * ms[unit as "s" | "m" | "h" | "d" | "w"]
}

// ── Pruning ────────────────────────────────────────────────────────────────────────────────────

function readStamp(dir: string): CloneStamp | undefined {
  try {
    const parsed: unknown = JSON.parse(readFileSync(join(dir, STAMP_REL), "utf8"))
    if (typeof parsed !== "object" || parsed === null) return undefined
    const s = parsed as Partial<CloneStamp>
    if (s.marker !== STAMP_MARKER || typeof s.lastUsedMs !== "number") return undefined
    return s as CloneStamp
  } catch {
    // ⚠️ An unreadable or malformed stamp means "not provably ours", which means not prunable.
    return undefined
  }
}

function writeStamp(dir: string, stamp: CloneStamp): void {
  writeFileSync(join(dir, STAMP_REL), JSON.stringify(stamp, null, 2) + "\n")
}

/**
 * Remove clones idle past the window.
 *
 * ⛔ THREE CONDITIONS, ALL REQUIRED, AND THIS IS THE WHOLE SAFETY ARGUMENT: the directory is a
 * DIRECT child of the clone root, it carries our own stamp, and its stamp is older than the window.
 * A directory we did not create has no stamp and is therefore never touched — the clone root may be
 * somewhere a person also keeps their own work, and a reviewer that eats it is unrecoverable.
 *
 * ⛔ The clone we are about to use is exempt even when its stamp is stale. Deleting it and cloning
 * it again in the same call is pure cost.
 */
export function pruneIdleClones(root: string, keepDirName: string, windowMs: number, nowMs: number): string[] {
  if (windowMs <= 0) return []
  const removed: string[] = []
  let entries: Dirent[]
  try {
    entries = readdirSync(root, { withFileTypes: true })
  } catch {
    return []
  }
  for (const e of entries) {
    if (!e.isDirectory()) continue
    if (e.name === keepDirName) continue
    const dir = join(root, e.name)
    const stamp = readStamp(dir)
    if (stamp === undefined) continue
    if (nowMs - stamp.lastUsedMs < windowMs) continue
    rmSync(dir, { recursive: true, force: true, maxRetries: 2 })
    removed.push(e.name)
  }
  return removed
}

// ── Preparing the clone ────────────────────────────────────────────────────────────────────────

/** `refs/pull/{pr}/merge` → `refs/pull/41/merge`. */
function renderRef(template: string, prNumber: number): string {
  if (!template.includes("{pr}")) {
    throw new CloneError(
      `ref template ${JSON.stringify(template)} does not contain {pr}`,
      "bad-ref-template",
    )
  }
  return template.replaceAll("{pr}", String(prNumber))
}

/**
 * Prepare the clone for one pull request and check it out detached.
 *
 * ⭐ `git init` + a targeted fetch rather than `git clone`. A clone drags in the default branch we
 * will never read — on a large repository that IS the cost — and leaves a tracking branch that
 * makes the clone "know what else exists", which `repo.single_branch = true` exists to prevent.
 * The fetched objects are identical; only the wasted ones are missing.
 */
export async function prepareClone(
  cfg: CloneConfig,
  repo: string,
  prNumber: number,
  /**
   * ⭐ The GitHub token, so a PRIVATE repository can be reviewed at all. Optional because a public
   * remote, an ssh remote and a local path each need nothing. It reaches git through the
   * environment — see `credentialEnv`, which exists to keep it out of argv.
   */
  token?: string,
): Promise<PreparedClone> {
  // ⛔ A ref name is a path. An unvalidated pull-request number renders into the template and
  // becomes `refs/pull/../../heads/main/merge`, which fetches something nobody asked to review.
  // Validated before it is interpolated, never after.
  if (!Number.isSafeInteger(prNumber) || prNumber <= 0) {
    throw new CloneError(
      `pull-request number must be a positive integer, got ${JSON.stringify(prNumber)}`,
      "bad-pr-number",
    )
  }

  const remote = remoteUrlFor(repo.trim() === "" ? cfg.repo.slug : repo)
  const gitEnv = credentialEnv(remote, token)
  const localRemote = localPathOf(remote)
  if (localRemote !== undefined) assertNotForbiddenTree(localRemote, "the repository at")

  const root = resolve(expandPath(cfg.repo.cloneRoot))
  assertNotForbiddenTree(root, "clone_root")
  assertRootOutsideEveryCheckout(root)
  mkdirSync(root, { recursive: true })

  const dirName = cloneDirName(remote)
  const dir = join(root, dirName)
  const pruned = pruneIdleClones(root, dirName, parseDuration(cfg.repo.pruneAfter), Date.now())

  // ── Reuse, or start clean ──
  let reused = false
  if (existsSync(dir)) {
    if (existsSync(join(dir, ".git")) && cfg.repo.reuse) {
      reused = true
    } else if (readStamp(dir) !== undefined || readdirSync(dir).length === 0) {
      // Ours, or empty. Safe to replace.
      rmSync(dir, { recursive: true, force: true, maxRetries: 2 })
    } else {
      // ⛔ Something is at our path that we did not create. Refuse rather than delete it.
      throw new CloneError(
        `${dir} exists, is not an sibyl clone, and is not empty — refusing to delete it`,
        "dirty-clone-path",
      )
    }
  }

  if (!reused) {
    await mustGit(undefined, ["init", "-q", dir])
  }

  const originUrl = await runGit(dir, ["remote", "get-url", "origin"])
  if (originUrl.exitCode !== 0) {
    await mustGit(dir, ["remote", "add", "origin", remote])
  } else if (originUrl.stdout.trim() !== remote) {
    await mustGit(dir, ["remote", "set-url", "origin", remote])
  }

  // ── Which ref actually exists ──
  const mergeRef = renderRef(cfg.repo.refTemplate, prNumber)
  const fallbackRef = renderRef(cfg.repo.fallbackRef, prNumber)

  // ⭐ One `ls-remote` for both refs, and we branch on its DOCUMENTED exit code (2 = no matching
  // ref), never on the wording of an error message. Matching "couldn't find remote ref" would be a
  // gate that silently inverts the day git rewords it or a locale changes.
  const probe = await runGit(dir, ["ls-remote", "--exit-code", "origin", mergeRef, fallbackRef], gitEnv)
  if (probe.exitCode !== 0 && probe.exitCode !== 2) {
    throw new CloneError(
      `git ls-remote origin failed (exit ${probe.exitCode}): ${probe.stderr.trim()}`,
      "git-failed",
    )
  }
  const present = new Set<string>()
  for (const line of probe.stdout.split("\n")) {
    const name = line.split("\t")[1]
    if (name !== undefined && name !== "") present.add(name.trim())
  }

  // ⭐ THE DECISION THIS MODULE EXISTS FOR. Merge when it is there; head when it is not, and say so.
  const useMerge = present.has(mergeRef)
  const ref = useMerge ? mergeRef : fallbackRef
  const usedFallbackRef = !useMerge
  if (!present.has(ref)) {
    throw new CloneError(
      `neither ${mergeRef} nor ${fallbackRef} exists on ${remote} — is pull request ${prNumber} real?`,
      "no-ref",
    )
  }

  // ── Fetch and check out ──
  const localRef = `refs/sibyl/pr/${prNumber}`
  const depthArgs: string[] = []
  if (cfg.repo.depth > 0) {
    depthArgs.push("--depth", String(cfg.repo.depth))
  } else if (existsSync(join(dir, ".git", "shallow"))) {
    // ⚠️ depth 0 means full, but a clone we made earlier at depth 50 stays shallow until it is told
    // otherwise, and blame against a shallow history silently reports the grafted commit.
    depthArgs.push("--unshallow")
  }
  await mustGit(dir, ["fetch", "--no-tags", "--force", ...depthArgs, "origin", `+${ref}:${localRef}`], gitEnv)
  await mustGit(dir, ["checkout", "--force", "--detach", localRef])

  // ⛔ A reused clone still holds the previous pull request's untracked files. Left in place they
  // are readable, greppable and citable, so a finding can quote a real line in a file that is not
  // in this pull request at all. Cleaned every run, including the first, so there is one code path.
  // ⛔ Guarded on the path being a direct child of our own clone root: `clean -ffdx` anywhere else
  // would be destroying someone's work.
  if (dirname(dir) !== root) {
    throw new CloneError(`refusing to clean ${dir}: not a direct child of ${root}`, "dirty-clone-path")
  }
  await mustGit(dir, ["clean", "-ffdx"])

  const sha = (await mustGit(dir, ["rev-parse", "HEAD"])).trim()

  writeStamp(dir, {
    marker: STAMP_MARKER,
    remote,
    lastUsedMs: Date.now(),
    lastPr: prNumber,
    lastRef: ref,
  })

  return { path: dir, sha, ref, usedFallbackRef, reused, pruned }
}

// ── Reading a cited line ───────────────────────────────────────────────────────────────────────

/**
 * Resolve a repository-relative path inside the clone, or throw.
 *
 * ⛔ TWO CHECKS, AND ONE OF THEM IS NOT ENOUGH. The lexical check catches `../../etc/passwd`; the
 * realpath check catches a symlink committed to the branch under review that points out of the
 * clone. `paths.follow_symlinks = false` is the stated policy — a symlink out of the clone is a
 * path escape — and this is where it is enforced, because this is the only function that opens a
 * file whose name came from a model.
 */
export function resolveInClone(clonePath: string, path: string): string {
  if (isAbsolute(path)) {
    throw new CloneError(`${path} is absolute; a cited path must be repository-relative`, "path-escape")
  }
  const root = resolve(clonePath)
  const abs = resolve(root, path)
  if (abs !== root && !abs.startsWith(root + sep)) {
    throw new CloneError(`${path} resolves outside the clone at ${root}`, "path-escape")
  }
  let realRoot: string
  try {
    realRoot = realpathSync(root)
  } catch {
    throw new CloneError(`clone path ${root} does not exist`, "path-escape")
  }
  let real: string
  try {
    real = realpathSync(abs)
  } catch {
    // Does not exist yet — the lexical check already proved it would have been inside.
    return abs
  }
  if (real !== realRoot && !real.startsWith(realRoot + sep)) {
    throw new CloneError(`${path} is a symlink pointing outside the clone at ${realRoot}`, "path-escape")
  }
  return abs
}

/**
 * The exact text of one line, for the byte-identical quote gate.
 *
 * ⭐ `undefined` for a missing file or a line past the end, but a THROW for a path escape. They are
 * different kinds of fact: a line that does not exist is a finding failing `citedLineExists`, which
 * is an ordinary outcome recorded on the finding, while a path escape is an attack or a bug and
 * must never be quietly reported as "that finding did not check out".
 *
 * ⛔ A file ending in a newline does NOT have one more line than it has lines. `"a\n".split("\n")`
 * is `["a", ""]`, so a naive read hands back `""` for line 2 — and `""` compared against a model's
 * quote fails the gate for the right reason by luck, while `citedLineExists` passes for a line that
 * is not there. The trailing element is dropped.
 *
 * ⭐ A CRLF terminator's CR is part of the TERMINATOR, not the content. A quote gate that demanded
 * an invisible trailing CR would reject every correct quote in a repository checked out on Windows.
 * Trailing SPACES and TABS are content and are preserved exactly — trailing whitespace is the whole
 * subject of a real class of finding.
 *
 * ⚠️ Decoded as UTF-8. A binary file would round-trip lossily, which is why `paths.exclude` drops
 * binaries before anything is cited.
 */
export function readFileAt(clonePath: string, path: string, line: number): string | undefined {
  if (!Number.isSafeInteger(line) || line < 1) {
    throw new CloneError(`line must be a positive integer, got ${JSON.stringify(line)}`, "path-escape")
  }
  const abs = resolveInClone(clonePath, path)
  let text: string
  try {
    text = readFileSync(abs, "utf8")
  } catch {
    return undefined
  }
  const lines = text.split("\n")
  if (lines.length > 0 && lines[lines.length - 1] === "") lines.pop()
  const raw = lines[line - 1]
  if (raw === undefined) return undefined
  return raw.endsWith("\r") ? raw.slice(0, -1) : raw
}
